import React, { useState } from 'react';
import { Plus, Trash2, Upload, Edit3, CalendarCheck, Clock, IndianRupee, Save, Languages, Sparkles, ChevronDown, ChevronUp, Star, Users, User, HeartHandshake, X, Check } from 'lucide-react';
import api, { formatApiError } from '../../lib/api';
import { useData } from '../../context/DataContext';
import { useToast } from '../../hooks/use-toast';

const emptyPuja = {
  slug: '', title_en: '', title_hi: '', description_en: '', description_hi: '',
  temple_en: '', temple_hi: '', date: '', time: '09:00',
  date_label_en: '', date_label_hi: '',
  image: '', price: 501, originalPrice: 0,
  benefits_en: [], benefits_hi: [], panditId: '', packages: [],
};

const DEFAULT_PACKAGES = [
  { id: 'individual', name_en: 'Individual', name_hi: 'व्यक्तिगत', price: 201, persons: 1, icon: 'User',
    desc_en: 'Pandit Ji will call out your name and gotra during the puja sankalp.',
    desc_hi: 'पंडित जी संकल्प के समय आपका नाम और गोत्र लेंगे।',
    features_en: ['1 Name & Gotra in Sankalp', 'Puja video recording', 'Optional Prasad delivery'],
    features_hi: ['संकल्प में 1 नाम व गोत्र', 'पूजा वीडियो रिकॉर्डिंग', 'वैकल्पिक प्रसाद डिलीवरी'],
    popular: false },
  { id: 'couple', name_en: '2 Person', name_hi: 'युगल', price: 351, persons: 2, icon: 'Users',
    desc_en: 'Pandit Ji will call out 2 Devotees name and gotra during the puja sankalp.',
    desc_hi: 'पंडित जी संकल्प में 2 भक्तों का नाम लेंगे।',
    features_en: ['2 Names & Gotra in Sankalp', 'Puja video recording', 'Optional Prasad delivery'],
    features_hi: ['संकल्प में 2 नाम व गोत्र', 'पूजा वीडियो रिकॉर्डिंग', 'वैकल्पिक प्रसाद डिलीवरी'],
    popular: false },
  { id: 'family', name_en: 'Family', name_hi: 'परिवार', price: 751, persons: 4, icon: 'Users',
    desc_en: 'Pandit Ji will call out 4 Devotees name and gotra of your family.',
    desc_hi: 'पंडित जी आपके परिवार के 4 भक्तों का नाम लेंगे।',
    features_en: ['4 Names & Gotra in Sankalp', 'Puja video recording', 'Free Prasad Delivery', 'Blessing certificate'],
    features_hi: ['संकल्प में 4 नाम व गोत्र', 'पूजा वीडियो रिकॉर्डिंग', 'निःशुल्क प्रसाद डिलीवरी', 'आशीर्वाद प्रमाणपत्र'],
    popular: true },
  { id: 'joint_family', name_en: 'Joint Family', name_hi: 'संयुक्त परिवार', price: 1151, persons: 6, icon: 'HeartHandshake',
    desc_en: 'Pandit Ji will call out 6 Devotees name and gotra of your family.',
    desc_hi: 'पंडित जी आपके परिवार के 6 भक्तों का नाम लेंगे।',
    features_en: ['6 Names & Gotra in Sankalp', 'Puja video recording', 'Free Prasad Delivery', 'Rudraksha Mala + Certificate'],
    features_hi: ['संकल्प में 6 नाम व गोत्र', 'पूजा वीडियो रिकॉर्डिंग', 'निःशुल्क प्रसाद डिलीवरी', 'रुद्राक्ष माला + प्रमाणपत्र'],
    popular: false },
];

const generateDateLabel = (dateStr, timeStr) => {
  if (!dateStr) return { en: '', hi: '' };
  const d = new Date(dateStr);
  const opts = { day: 'numeric', month: 'long', year: 'numeric' };
  const en = `${d.toLocaleDateString('en-IN', opts)}${timeStr ? ` at ${timeStr}` : ''}`;
  const hi = `${d.toLocaleDateString('hi-IN', opts)}${timeStr ? ` • ${timeStr}` : ''}`;
  return { en, hi };
};

export default function PujasTab() {
  const { pujas, pandits, refresh } = useData();
  const { toast } = useToast();
  const [form, setForm] = useState(emptyPuja);
  const [editingId, setEditingId] = useState(null);
  const [show, setShow] = useState(false);
  const [translating, setTranslating] = useState(false);

  const openNew = () => {
    setEditingId(null);
    setForm({ ...emptyPuja, packages: DEFAULT_PACKAGES.map((p) => ({ ...p })) });
    setShow(true);
    window.scrollTo({ top: 300, behavior: 'smooth' });
  };
  const openEdit = (p) => {
    setEditingId(p.id);
    const pkgs = (p.packages && p.packages.length) ? p.packages : DEFAULT_PACKAGES.map((x) => ({ ...x }));
    setForm({ ...emptyPuja, ...p, time: p.time || '09:00', packages: pkgs, benefits_en: p.benefits_en || [], benefits_hi: p.benefits_hi || [] });
    setShow(true);
    window.scrollTo({ top: 300, behavior: 'smooth' });
  };

  const del = async (id) => {
    if (!window.confirm('Delete this puja?')) return;
    try {
      await api.delete(`/api/pujas/${id}`);
      await refresh();
      toast({ title: 'Puja deleted' });
    } catch (e) { toast({ title: formatApiError(e), variant: 'destructive' }); }
  };

  const uploadFile = (e) => {
    const f = e.target.files[0]; if (!f) return;
    const r = new FileReader();
    r.onload = () => setForm((prev) => ({ ...prev, image: r.result }));
    r.readAsDataURL(f);
  };

  // ---- Auto-translate helpers ----
  const translateOne = async (text, target = 'hi', source = 'en') => {
    if (!text || !text.trim()) return '';
    const { data } = await api.post('/api/translate', { text, target, source });
    return data.translated || '';
  };

  const translateFieldToHindi = async (fieldEn, fieldHi) => {
    if (!form[fieldEn] || !form[fieldEn].trim()) {
      toast({ title: 'Enter English text first', variant: 'destructive' }); return;
    }
    setTranslating(true);
    try {
      const hi = await translateOne(form[fieldEn]);
      setForm((prev) => ({ ...prev, [fieldHi]: hi }));
      toast({ title: '✓ Translated to Hindi' });
    } catch (e) { toast({ title: formatApiError(e), variant: 'destructive' }); }
    finally { setTranslating(false); }
  };

  const translateAllToHindi = async () => {
    setTranslating(true);
    try {
      const items = {
        title: form.title_en, description: form.description_en, temple: form.temple_en,
      };
      const { data } = await api.post('/api/translate/batch', { items, target: 'hi', source: 'en' });
      const t = data.translated || {};
      const benefits_hi = [];
      for (const b of form.benefits_en) benefits_hi.push(await translateOne(b));
      // translate packages too
      const packages = [];
      for (const pkg of form.packages) {
        const pkgHi = { ...pkg };
        if (pkg.name_en) pkgHi.name_hi = await translateOne(pkg.name_en);
        if (pkg.desc_en) pkgHi.desc_hi = await translateOne(pkg.desc_en);
        pkgHi.features_hi = [];
        for (const f of (pkg.features_en || [])) pkgHi.features_hi.push(await translateOne(f));
        packages.push(pkgHi);
      }
      setForm((prev) => ({
        ...prev,
        title_hi: t.title || prev.title_hi,
        description_hi: t.description || prev.description_hi,
        temple_hi: t.temple || prev.temple_hi,
        benefits_hi,
        packages,
      }));
      toast({ title: '✓ Translated all fields to Hindi' });
    } catch (e) { toast({ title: formatApiError(e), variant: 'destructive' }); }
    finally { setTranslating(false); }
  };

  const translateBenefit = async (idx) => {
    if (!form.benefits_en[idx]) return;
    setTranslating(true);
    try {
      const hi = await translateOne(form.benefits_en[idx]);
      const arr = [...form.benefits_hi]; arr[idx] = hi;
      setForm((p) => ({ ...p, benefits_hi: arr }));
    } catch (e) { toast({ title: formatApiError(e), variant: 'destructive' }); }
    finally { setTranslating(false); }
  };

  // ---- Benefits ----
  const addBenefit = () => setForm((p) => ({ ...p, benefits_en: [...p.benefits_en, ''], benefits_hi: [...p.benefits_hi, ''] }));
  const setBenefit = (idx, lang, val) => {
    const key = lang === 'en' ? 'benefits_en' : 'benefits_hi';
    const arr = [...form[key]]; arr[idx] = val;
    setForm((p) => ({ ...p, [key]: arr }));
  };
  const delBenefit = (idx) => {
    setForm((p) => ({
      ...p,
      benefits_en: p.benefits_en.filter((_, i) => i !== idx),
      benefits_hi: p.benefits_hi.filter((_, i) => i !== idx),
    }));
  };

  // ---- Packages ----
  const addPackage = () => setForm((p) => ({ ...p, packages: [...p.packages, {
    id: `custom_${Date.now()}`, name_en: 'New Package', name_hi: 'नया पैकेज', price: 101, persons: 1, icon: 'User',
    desc_en: '', desc_hi: '', features_en: [], features_hi: [], popular: false,
  }] }));
  const setPkg = (idx, key, val) => {
    const arr = [...form.packages]; arr[idx] = { ...arr[idx], [key]: val };
    setForm((p) => ({ ...p, packages: arr }));
  };
  const delPkg = (idx) => setForm((p) => ({ ...p, packages: p.packages.filter((_, i) => i !== idx) }));
  const movePkg = (idx, dir) => {
    const arr = [...form.packages];
    const j = idx + dir; if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setForm((p) => ({ ...p, packages: arr }));
  };
  const translatePkgField = async (idx, fieldEn, fieldHi) => {
    const pkg = form.packages[idx];
    if (!pkg[fieldEn]) return;
    setTranslating(true);
    try {
      const hi = await translateOne(pkg[fieldEn]);
      setPkg(idx, fieldHi, hi);
    } catch (e) { toast({ title: formatApiError(e), variant: 'destructive' }); }
    finally { setTranslating(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    const slug = form.slug || form.title_en.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    let { date_label_en, date_label_hi } = form;
    if (!date_label_en || !date_label_hi) {
      const gen = generateDateLabel(form.date, form.time);
      date_label_en = date_label_en || gen.en;
      date_label_hi = date_label_hi || gen.hi;
    }
    const payload = { ...form, slug, date_label_en, date_label_hi };
    try {
      if (editingId) await api.put(`/api/pujas/${editingId}`, payload);
      else await api.post('/api/pujas', payload);
      await refresh();
      toast({ title: editingId ? 'Puja updated' : 'Puja added' });
      setShow(false);
    } catch (err) { toast({ title: formatApiError(err), variant: 'destructive' }); }
  };

  const assignPandit = async (puja, panditId) => {
    try {
      await api.put(`/api/pujas/${puja.id}`, { ...puja, panditId });
      await refresh();
      const pName = pandits.find((pd) => pd.id === panditId)?.name_en || 'None';
      toast({ title: `Assigned to ${pName}` });
    } catch (err) { toast({ title: formatApiError(err), variant: 'destructive' }); }
  };

  return (
    <>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <p className="text-sm text-nv-muted">Manage pujas — packages, benefits, discount price & auto-translate</p>
        <button data-testid="add-puja-btn" onClick={openNew} className="btn-orange rounded-full px-4 md:px-5 py-2.5 font-semibold flex items-center gap-2 text-sm">
          <Plus className="w-4 h-4" /> Add Puja
        </button>
      </div>

      {show && (
        <form onSubmit={save} className="bg-white border border-orange-200 rounded-2xl p-4 md:p-6 mb-6 shadow-md">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="text-lg font-bold text-orange-800 flex items-center gap-2">
              {editingId ? <Edit3 className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
              {editingId ? 'Edit Puja' : 'New Puja'}
            </h3>
            <button
              type="button" onClick={translateAllToHindi} disabled={translating}
              data-testid="auto-translate-all"
              className="rounded-full px-4 py-2 text-sm font-semibold border-2 border-purple-300 text-purple-700 bg-purple-50 hover:bg-purple-100 flex items-center gap-2 disabled:opacity-60"
            >
              <Languages className="w-4 h-4" /> {translating ? 'Translating…' : 'Auto-translate all to Hindi'}
            </button>
          </div>

          {/* Title (en/hi) */}
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <LabeledInput label="Title (English)" required value={form.title_en} onChange={(v) => setForm({ ...form, title_en: v })} />
            <BilingualHindiInput
              label="Title (Hindi)" value={form.title_hi}
              onChange={(v) => setForm({ ...form, title_hi: v })}
              onTranslate={() => translateFieldToHindi('title_en', 'title_hi')}
              busy={translating}
            />
            <LabeledInput label="Temple / Location (English)" required value={form.temple_en} onChange={(v) => setForm({ ...form, temple_en: v })} />
            <BilingualHindiInput
              label="Temple / Location (Hindi)" value={form.temple_hi}
              onChange={(v) => setForm({ ...form, temple_hi: v })}
              onTranslate={() => translateFieldToHindi('temple_en', 'temple_hi')}
              busy={translating}
            />
          </div>

          {/* Date + Price + Original Price */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 bg-orange-50/70 border border-orange-200 rounded-xl p-3 mb-4">
            <div>
              <label className="text-xs font-semibold text-orange-800 mb-1 flex items-center gap-1"><CalendarCheck className="w-3.5 h-3.5" /> Puja Date</label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required className="w-full border border-orange-300 rounded-lg px-3 py-2 text-sm bg-white" />
            </div>
            <div>
              <label className="text-xs font-semibold text-orange-800 mb-1 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Puja Time</label>
              <input type="time" value={form.time || '09:00'} onChange={(e) => setForm({ ...form, time: e.target.value })} className="w-full border border-orange-300 rounded-lg px-3 py-2 text-sm bg-white" />
            </div>
            <div>
              <label className="text-xs font-semibold text-orange-800 mb-1 flex items-center gap-1"><IndianRupee className="w-3.5 h-3.5" /> Sankalp Price (₹)</label>
              <input type="number" min="1" value={form.price} onChange={(e) => setForm({ ...form, price: +e.target.value })} required className="w-full border border-orange-300 rounded-lg px-3 py-2 text-sm bg-white font-semibold text-orange-700" />
            </div>
            <div>
              <label className="text-xs font-semibold text-orange-800 mb-1 flex items-center gap-1"><IndianRupee className="w-3.5 h-3.5" /> Original Price (₹)</label>
              <input type="number" min="0" value={form.originalPrice || ''} onChange={(e) => setForm({ ...form, originalPrice: +e.target.value })} placeholder="Higher than price for discount" className="w-full border border-orange-300 rounded-lg px-3 py-2 text-sm bg-white" />
              {form.originalPrice > form.price && (
                <p className="text-[10px] text-green-700 mt-1">
                  {Math.round(((form.originalPrice - form.price) / form.originalPrice) * 100)}% OFF
                </p>
              )}
            </div>
          </div>

          {/* Pandit assign */}
          <div className="mb-4">
            <label className="text-xs font-medium text-stone-600 mb-1 block">Assign Pandit Ji</label>
            <select value={form.panditId || ''} onChange={(e) => setForm({ ...form, panditId: e.target.value })} className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">-- Not assigned yet --</option>
              {pandits.map((pd) => (
                <option key={pd.id} value={pd.id}>{pd.name_en} ({pd.experience} yrs) — {pd.temple_en}</option>
              ))}
            </select>
          </div>

          {/* Descriptions */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <LabeledTextarea label="Description (English)" value={form.description_en} onChange={(v) => setForm({ ...form, description_en: v })} rows={3} />
            <BilingualHindiTextarea
              label="विवरण (हिन्दी)" value={form.description_hi}
              onChange={(v) => setForm({ ...form, description_hi: v })}
              onTranslate={() => translateFieldToHindi('description_en', 'description_hi')}
              busy={translating} rows={3}
            />
          </div>

          {/* Benefits editor */}
          <Section title="Benefits" subtitle="Shown as bullet list on the puja page. Add as many as you need.">
            <div className="space-y-2">
              {form.benefits_en.length === 0 && (
                <p className="text-xs text-nv-muted italic">No benefits yet — click "Add benefit" below.</p>
              )}
              {form.benefits_en.map((b, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-2 items-center">
                  <input
                    value={b} onChange={(e) => setBenefit(i, 'en', e.target.value)} placeholder="English benefit"
                    className="border border-orange-200 rounded-lg px-3 py-2 text-sm" data-testid={`benefit-en-${i}`}
                  />
                  <div className="flex gap-1 items-center">
                    <input
                      value={form.benefits_hi[i] || ''} onChange={(e) => setBenefit(i, 'hi', e.target.value)} placeholder="हिन्दी लाभ"
                      className="flex-1 border border-orange-200 rounded-lg px-3 py-2 text-sm" data-testid={`benefit-hi-${i}`}
                    />
                    <button type="button" onClick={() => translateBenefit(i)} disabled={translating} title="Translate this benefit"
                      className="p-2 rounded-lg border border-purple-300 text-purple-700 hover:bg-purple-50 disabled:opacity-50">
                      <Languages className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => delBenefit(i)} className="p-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button type="button" onClick={addBenefit} data-testid="add-benefit-btn"
              className="mt-3 rounded-full px-4 py-1.5 text-xs font-semibold border-2 border-orange-300 text-orange-700 hover:bg-orange-50 inline-flex items-center gap-1">
              <Plus className="w-3 h-3" /> Add benefit
            </button>
          </Section>

          {/* Packages editor */}
          <Section title="Participation Packages" subtitle="These packages appear on the puja detail page. Reorder, edit, add or remove.">
            <div className="space-y-4">
              {form.packages.map((pkg, i) => (
                <PackageRow
                  key={pkg.id + i}
                  index={i}
                  pkg={pkg}
                  onChange={(k, v) => setPkg(i, k, v)}
                  onDelete={() => delPkg(i)}
                  onMoveUp={() => movePkg(i, -1)}
                  onMoveDown={() => movePkg(i, +1)}
                  onTranslate={(field) => translatePkgField(i, field + '_en', field + '_hi')}
                  translating={translating}
                  translateOne={translateOne}
                  setPkg={(k, v) => setPkg(i, k, v)}
                  toastFn={toast}
                />
              ))}
            </div>
            <button type="button" onClick={addPackage} data-testid="add-package-btn"
              className="mt-4 rounded-full px-5 py-2 text-sm font-semibold border-2 border-orange-400 text-orange-700 hover:bg-orange-50 inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add package
            </button>
          </Section>

          {/* Image */}
          <div className="mt-6">
            <label className="text-xs font-medium text-stone-600 mb-1 block">Image (URL or upload)</label>
            <div className="flex gap-2">
              <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://..." className="flex-1 border border-orange-200 rounded-lg px-3 py-2 text-sm" />
              <label className="px-3 py-2 rounded-lg border border-orange-300 text-orange-700 text-sm cursor-pointer hover:bg-orange-50 flex items-center gap-1">
                <Upload className="w-4 h-4" /> Upload
                <input type="file" accept="image/*" onChange={uploadFile} className="hidden" />
              </label>
            </div>
            {form.image && <img src={form.image} alt="" className="mt-2 h-32 rounded-lg object-cover" />}
          </div>

          <div className="flex flex-wrap gap-3 justify-end pt-6 border-t border-orange-100 mt-6">
            <button type="button" onClick={() => setShow(false)} className="px-5 py-2 rounded-lg border border-stone-300 text-stone-700">Cancel</button>
            <button data-testid="save-puja-btn" type="submit" className="btn-orange px-6 py-2 rounded-lg font-semibold flex items-center gap-2"><Save className="w-4 h-4" /> Save Puja</button>
          </div>
        </form>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pujas-list">
        {pujas.map((p) => {
          const assigned = pandits.find((pd) => pd.id === p.panditId);
          const hasDisc = p.originalPrice && p.originalPrice > p.price;
          return (
            <div key={p.id} className="bg-white border border-nv-gold/20 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition">
              <img src={p.image} alt="" className="w-full h-32 object-cover" />
              <div className="p-4">
                <h4 className="font-semibold text-sm text-nv-wine line-clamp-2 mb-1">{p.title_en}</h4>
                <div className="flex items-center gap-2 text-xs text-nv-muted mb-1">
                  <CalendarCheck className="w-3 h-3" /> {p.date} {p.time && <><Clock className="w-3 h-3 ml-1" /> {p.time}</>}
                </div>
                <div className="flex items-baseline gap-2 mb-2">
                  <div className="text-orange-700 font-bold text-base">₹ {p.price}</div>
                  {hasDisc && (
                    <>
                      <div className="text-stone-400 line-through text-xs">₹ {p.originalPrice}</div>
                      <div className="text-[10px] font-bold text-green-700 bg-green-100 rounded px-1.5 py-0.5">
                        {Math.round(((p.originalPrice - p.price) / p.originalPrice) * 100)}% OFF
                      </div>
                    </>
                  )}
                </div>
                <div className="text-[10px] text-nv-muted mb-2">
                  {(p.packages?.length || 0)} packages • {(p.benefits_en?.length || 0)} benefits
                </div>

                <div className="mb-3">
                  <label className="text-[10px] uppercase tracking-wider text-orange-700 font-semibold">Pandit Ji</label>
                  <select value={p.panditId || ''} onChange={(e) => assignPandit(p, e.target.value)} className="w-full mt-1 border border-orange-200 rounded-md px-2 py-1.5 text-xs bg-white">
                    <option value="">-- Not assigned --</option>
                    {pandits.map((pd) => (<option key={pd.id} value={pd.id}>{pd.name_en}</option>))}
                  </select>
                  {assigned && (<p className="text-[10px] text-green-700 mt-1 flex items-center gap-1">✓ Assigned • {assigned.experience} yrs exp</p>)}
                </div>

                <div className="flex gap-2">
                  <button data-testid={`edit-puja-${p.id}`} onClick={() => openEdit(p)} className="flex-1 border border-orange-300 text-orange-700 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1 hover:bg-orange-50">
                    <Edit3 className="w-3 h-3" /> Edit
                  </button>
                  <button data-testid={`delete-puja-${p.id}`} onClick={() => del(p.id)} className="flex-1 border border-red-300 text-red-600 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1 hover:bg-red-50">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ------- Reusable form bits ------- */
function LabeledInput({ label, value, onChange, required, placeholder }) {
  return (
    <div>
      <label className="text-xs font-medium text-stone-600 mb-1 block">{label}</label>
      <input value={value || ''} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder} className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm" />
    </div>
  );
}
function LabeledTextarea({ label, value, onChange, rows = 2 }) {
  return (
    <div>
      <label className="text-xs font-medium text-stone-600 mb-1 block">{label}</label>
      <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={rows} className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm" />
    </div>
  );
}
function BilingualHindiInput({ label, value, onChange, onTranslate, busy }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-medium text-stone-600">{label}</label>
        <button type="button" onClick={onTranslate} disabled={busy}
          className="text-[10px] font-semibold text-purple-700 hover:text-purple-900 inline-flex items-center gap-1 disabled:opacity-50">
          <Languages className="w-3 h-3" /> Auto
        </button>
      </div>
      <input value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm" />
    </div>
  );
}
function BilingualHindiTextarea({ label, value, onChange, onTranslate, busy, rows = 2 }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-medium text-stone-600">{label}</label>
        <button type="button" onClick={onTranslate} disabled={busy}
          className="text-[10px] font-semibold text-purple-700 hover:text-purple-900 inline-flex items-center gap-1 disabled:opacity-50">
          <Languages className="w-3 h-3" /> Auto
        </button>
      </div>
      <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={rows} className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm" />
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="mt-6 border-t border-orange-100 pt-6">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-orange-600" />
        <h4 className="font-bold text-orange-800">{title}</h4>
      </div>
      {subtitle && <p className="text-xs text-nv-muted mb-4">{subtitle}</p>}
      {children}
    </div>
  );
}

function PackageRow({ index, pkg, onChange, onDelete, onMoveUp, onMoveDown, onTranslate, translating, translateOne, setPkg, toastFn }) {
  const [open, setOpen] = useState(true);

  const setFeature = (lang, idx, val) => {
    const key = 'features_' + lang;
    const arr = [...(pkg[key] || [])]; arr[idx] = val;
    onChange(key, arr);
  };
  const addFeature = () => {
    onChange('features_en', [...(pkg.features_en || []), '']);
    onChange('features_hi', [...(pkg.features_hi || []), '']);
  };
  const delFeature = (idx) => {
    onChange('features_en', (pkg.features_en || []).filter((_, i) => i !== idx));
    onChange('features_hi', (pkg.features_hi || []).filter((_, i) => i !== idx));
  };
  const translateFeature = async (idx) => {
    const en = (pkg.features_en || [])[idx];
    if (!en) return;
    try {
      const hi = await translateOne(en);
      setFeature('hi', idx, hi);
    } catch (e) { toastFn({ title: 'Translation failed', variant: 'destructive' }); }
  };

  return (
    <div className="border border-orange-200 rounded-xl overflow-hidden bg-orange-50/30">
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-orange-100/60">
        <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-2 flex-1 min-w-0 text-left">
          {open ? <ChevronUp className="w-4 h-4 text-orange-700 shrink-0" /> : <ChevronDown className="w-4 h-4 text-orange-700 shrink-0" />}
          <span className="font-semibold text-sm text-orange-900 truncate">
            {index + 1}. {pkg.name_en || '(untitled)'} — ₹{pkg.price}
          </span>
          {pkg.popular && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-orange-500 text-white uppercase inline-flex items-center gap-1"><Star className="w-2.5 h-2.5 fill-white" /> Popular</span>}
        </button>
        <div className="flex items-center gap-1">
          <button type="button" onClick={onMoveUp} className="p-1 rounded hover:bg-orange-200" title="Move up"><ChevronUp className="w-3.5 h-3.5" /></button>
          <button type="button" onClick={onMoveDown} className="p-1 rounded hover:bg-orange-200" title="Move down"><ChevronDown className="w-3.5 h-3.5" /></button>
          <button type="button" onClick={onDelete} data-testid={`del-pkg-${index}`} className="p-1 rounded hover:bg-red-100 text-red-600" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      {open && (
        <div className="p-3 md:p-4 space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <LabeledInput label="Name (English)" value={pkg.name_en} onChange={(v) => onChange('name_en', v)} required />
            <BilingualHindiInput label="Name (Hindi)" value={pkg.name_hi} onChange={(v) => onChange('name_hi', v)} onTranslate={() => onTranslate('name')} busy={translating} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-medium text-stone-600 mb-1 block">Price (₹)</label>
              <input type="number" min="1" value={pkg.price} onChange={(e) => onChange('price', +e.target.value)} className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600 mb-1 block">Persons</label>
              <input type="number" min="1" value={pkg.persons} onChange={(e) => onChange('persons', +e.target.value)} className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600 mb-1 block">Icon</label>
              <select value={pkg.icon} onChange={(e) => onChange('icon', e.target.value)} className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="User">User</option>
                <option value="Users">Users</option>
                <option value="HeartHandshake">Heart</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-xs font-medium text-stone-700 cursor-pointer">
                <input type="checkbox" checked={!!pkg.popular} onChange={(e) => onChange('popular', e.target.checked)} className="w-4 h-4 accent-orange-500" />
                Mark popular
              </label>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <LabeledTextarea label="Description (English)" value={pkg.desc_en} onChange={(v) => onChange('desc_en', v)} rows={2} />
            <BilingualHindiTextarea label="विवरण (Hindi)" value={pkg.desc_hi} onChange={(v) => onChange('desc_hi', v)} onTranslate={() => onTranslate('desc')} busy={translating} rows={2} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-orange-800">Features (bullet list)</label>
              <button type="button" onClick={addFeature} className="text-[10px] font-semibold text-orange-700 hover:text-orange-900 inline-flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add feature
              </button>
            </div>
            {(pkg.features_en || []).length === 0 && (
              <p className="text-[11px] text-nv-muted italic">No features yet.</p>
            )}
            <div className="space-y-1.5">
              {(pkg.features_en || []).map((f, fi) => (
                <div key={fi} className="grid grid-cols-1 md:grid-cols-2 gap-1.5 items-center">
                  <input value={f} onChange={(e) => setFeature('en', fi, e.target.value)} placeholder="English feature" className="border border-orange-200 rounded px-2 py-1 text-xs" />
                  <div className="flex gap-1">
                    <input value={(pkg.features_hi || [])[fi] || ''} onChange={(e) => setFeature('hi', fi, e.target.value)} placeholder="हिन्दी" className="flex-1 border border-orange-200 rounded px-2 py-1 text-xs" />
                    <button type="button" onClick={() => translateFeature(fi)} className="p-1.5 rounded border border-purple-300 text-purple-700 hover:bg-purple-50" title="Translate">
                      <Languages className="w-3 h-3" />
                    </button>
                    <button type="button" onClick={() => delFeature(fi)} className="p-1.5 rounded border border-red-300 text-red-600 hover:bg-red-50" title="Delete">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
